import React, { Dispatch, SetStateAction } from 'react';
import { PaginationType, emptyPagination, mapPCPs, useLocalStorageState } from '~/util';
import {
    AuthContext,
    AuthContextType,
    ClinicList,
    PCPList,
    SearchContext,
    SearchContextType,
} from '~/services';
import { OptionWithDescriptionType } from '~/components/Select';

export interface ReminderFilterType {
    dueDateStart: null | string;
    dueDateEnd: null | string;
    viewStatus: string[];
    priority: null | string;
    createdByType: null | 'user' | 'system';
    hasOpenGap: boolean;
    clinic: ClinicList;
    PCP: PCPList;
    measureCode: OptionWithDescriptionType[];
}
interface ReminderSort {
    field: string;
    dir: string;
}
export interface ReminderContextType {
    filterValues: ReminderFilterType;
    setFilterValues: Dispatch<SetStateAction<ReminderFilterType>>;
    byMe: boolean;
    setCreatedByMe: Dispatch<SetStateAction<boolean>>;
    sort: ReminderSort;
    updateSort: (field: string) => void;
    resetSort: () => void;
    paginationRef: React.MutableRefObject<PaginationType>;
    resetPagination: () => void;
    fieldIsFiltered: (
        field: keyof typeof initialReminderFilters,
        value: ReminderFilterType[keyof ReminderFilterType]
    ) => boolean;
    clinicList: ClinicList;
    pcpList: PCPList;
}

const ReminderContext = React.createContext<ReminderContextType | null>(null);

export const initialReminderFilters: ReminderFilterType = {
    dueDateStart: null,
    dueDateEnd: null,
    viewStatus: ['open'],
    createdByType: null,
    hasOpenGap: false,
    clinic: [],
    PCP: [],
    priority: null,
    measureCode: [],
};

function ReminderProvider(props: { children: JSX.Element }) {
    const { authenticated } = React.useContext(AuthContext) as AuthContextType;
    const { clinicList } = React.useContext(SearchContext) as SearchContextType;
    const pcpList = React.useMemo(() => mapPCPs(clinicList), [clinicList]);
    const [filterValues, setFilterValues] = React.useState<ReminderFilterType>({
        ...initialReminderFilters,
    });
    const { item: createdByMe, setItem: setCreatedByMe } = useLocalStorageState<string | boolean>(
        'reminders-created-by-me',
        false
    );
    const byMe = typeof createdByMe === 'string' ? JSON.parse(createdByMe) : createdByMe;
    const paginationRef = React.useRef<PaginationType>(emptyPagination());
    const [sort, setSort] = React.useState({ field: 'due_at', dir: 'ASC' });

    function resetPagination() {
        paginationRef.current = {
            ...emptyPagination(),
            items_per_page: paginationRef.current.items_per_page,
        };
    }

    function fieldIsFiltered(
        field: keyof typeof initialReminderFilters,
        value: ReminderFilterType[keyof ReminderFilterType]
    ) {
        return value !== initialReminderFilters[field];
    }

    function getSortDirection(field: string, prev: ReminderSort) {
        if (prev.field === field) {
            if (prev.dir === 'ASC') return 'DESC';
            return 'ASC';
        }
        return 'ASC';
    }

    function updateSort(field: string) {
        setSort((prev) => ({
            field,
            dir: getSortDirection(field, prev),
        }));
    }

    const resetSort = React.useCallback(() => {
        setSort({ field: 'due_at', dir: 'ASC' });
    }, []);

    React.useEffect(() => {
        if (!authenticated) resetSort();
    }, [authenticated, resetSort]);

    const methods: ReminderContextType = {
        filterValues,
        setFilterValues,
        byMe,
        setCreatedByMe,
        sort,
        updateSort,
        resetSort,
        paginationRef,
        resetPagination,
        fieldIsFiltered,
        clinicList,
        pcpList,
    };
    return <ReminderContext.Provider value={methods} {...props} />;
}

export { ReminderContext, ReminderProvider };
